const LAT_LNG_REGEXP = /POINT \(([-+]?\d*\.?\d+) ([-+]?\d*\.?\d+)\)/;

function parseLatLng(pointString) {
  if (!pointString) return null;
  const match = pointString.match(LAT_LNG_REGEXP);
  if (!match) return null;
  return { lng: parseFloat(match[1]), lat: parseFloat(match[2]) };
}

function coordInLocale({ lat, lng }, org) {
  const bounds = org.serviceLocale.geo.bounds;
  const latInLocale = bounds.lat.min <= lat && lat <= bounds.lat.max;
  const lngInLocale = bounds.lng.min <= lng && lng <= bounds.lng.max;
  return latInLocale && lngInLocale;
}

function geocode(address, org) {
  // Test hook: allow Cucumber to override geocoder results
  if (window.testGeocoderOverride) {
    const o = window.testGeocoderOverride;
    return Promise.resolve([{
      geometry: { location: { lat: () => o.lat, lng: () => o.lng } },
      types: ["locality"],
      address_components: [
        { long_name: o.city || "", types: ["locality"] },
        { long_name: o.state || "", types: ["administrative_area_level_1"] },
      ],
    }]);
  }

  return new Promise((resolve, reject) => {
    let fullAddress = address;
    if (
      fullAddress.indexOf(org.serviceLocale.abbr) < 0 &&
      fullAddress.indexOf(org.serviceLocale.stateName) < 0
    ) {
      fullAddress += ", " + org.serviceLocale.stateName;
    }

    const opts = {
      address: fullAddress,
      componentRestrictions: { country: "US" },
      newForwardGeocoder: true,
    };

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(opts, (results, status) => {
      if (status === google.maps.GeocoderStatus.UNKNOWN_ERROR) {
        // Retry once on transient error
        geocoder.geocode(opts, (retryResults, retryStatus) => {
          if (retryStatus === google.maps.GeocoderStatus.OK) {
            resolve(retryResults);
          } else {
            reject(retryStatus);
          }
        });
      } else if (status === google.maps.GeocoderStatus.OK) {
        resolve(results);
      } else {
        reject(status);
      }
    });
  });
}

function reverseGeocode(latLng) {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ latLng }, (results, status) => {
      if (
        status === google.maps.GeocoderStatus.ZERO_RESULTS ||
        status === google.maps.GeocoderStatus.UNKNOWN_ERROR
      ) {
        geocoder.geocode({ latLng }, (retryResults, retryStatus) => {
          if (retryStatus === google.maps.GeocoderStatus.OK) {
            resolve(retryResults);
          } else {
            reject(retryStatus);
          }
        });
      } else if (status === google.maps.GeocoderStatus.OK) {
        resolve(results);
      } else {
        reject(status);
      }
    });
  });
}

function getLocationFromResults(results, org) {
  const cityResult = results[0].address_components.find(
    (addr) => addr.types && addr.types.includes("locality")
  );
  const stateResult = results[0].address_components.find(
    (addr) => addr.types && addr.types.includes("administrative_area_level_1")
  );

  if (
    stateResult &&
    stateResult.long_name === org.serviceLocale.stateName &&
    cityResult &&
    cityResult.long_name
  ) {
    return `${cityResult.long_name}, ${org.serviceLocale.abbr}`;
  }
  return "";
}

function getServiceArea(results) {
  const serviceAreaTypes = [
    "locality",
    "administrative_area_level_2",
    "administrative_area_level_1",
  ];
  if (serviceAreaTypes.includes(results[0].types[0])) {
    return results[0].address_components[0].long_name;
  }
  const city = results[0].address_components.find((component) =>
    serviceAreaTypes.some((t) => component.types.includes(t))
  );
  return city ? city.long_name : "";
}

function geolocate() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => resolve(null)
    );
  });
}

function haversine(start, end, options = {}) {
  if (!start || !start.lat || !start.lng || !end || !end.lat || !end.lng) {
    return null;
  }

  const toRad = (num) => (num * Math.PI) / 180;
  const R = options.unit === "mile" ? 3960 : 6371;

  const dLat = toRad(end.lat - start.lat);
  const dLon = toRad(end.lng - start.lng);
  const lat1 = toRad(start.lat);
  const lat2 = toRad(end.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  if (options.threshold) {
    return options.threshold > R * c;
  }
  return R * c;
}

function normalizeWebsite(url) {
  if (!url || /^http/.test(url)) return url;
  return `http://${url}`;
}

function isValidPhone(phone) {
  if (!phone) return false;
  return phone.replace(/\D*/g, "").length >= 10;
}

function containsAnEmailAddress(email) {
  if (!email) return false;
  return /\b\s*\S+@\S+\s*\b/.test(email);
}

function getPageResultCount(totalResults, resultsPerPage, currentPage, lastPage) {
  if (totalResults <= resultsPerPage) {
    return `1\u2013${totalResults} of ${totalResults} ${totalResults === 1 ? "result" : "results"}`;
  } else if (currentPage === lastPage) {
    return `${(currentPage * resultsPerPage - resultsPerPage) + 1}\u2013${totalResults} of ${totalResults} results`;
  } else {
    return `${(currentPage * resultsPerPage - resultsPerPage) + 1}\u2013${currentPage * resultsPerPage} of ${totalResults} results`;
  }
}

export {
  LAT_LNG_REGEXP,
  parseLatLng,
  coordInLocale,
  geocode,
  reverseGeocode,
  getLocationFromResults,
  getServiceArea,
  geolocate,
  haversine,
  normalizeWebsite,
  isValidPhone,
  containsAnEmailAddress,
  getPageResultCount,
};
